import {action, computed, makeObservable, observable} from "mobx";

import {AdminApi} from "../../../api/api";
import {TagAddInput} from "../../../api/values/tag-add-input";
import {TagSearchInput} from "../../../api/values/tag-search-input";
import {conflictChecker} from "../../../utils/conflict-checker-util";
import {validateLength} from "../../../utils/validation/functions/length";
import {validateSlug} from "../../../utils/validation/functions/slug";
import {createValidator} from "../../../utils/validation/validate";
import {ValidationResultEmpty} from "../../../utils/validation/validation-result";

export class TagAddStore {
    @observable
    public mediaId: string = "";
    @observable
    public isCreating = false;

    @observable
    public isOpen = false;

    @observable
    public label = "";

    @observable
    public slug = "";

    @observable
    public labelValidationResult = ValidationResultEmpty;

    @observable
    public slugValidationResult = ValidationResultEmpty;

    @observable
    public isLabelConflicted: boolean = false;

    @observable
    public isSlugConflicted: boolean = false;

    @observable
    public isLabelChecking: boolean = false;

    @observable
    public isSlugChecking: boolean = false;

    private onCreatedCallback?: () => void;

    private adminApi: AdminApi;

    constructor(options: {adminApi: AdminApi; mediaId: string}) {
        this.adminApi = options.adminApi;
        this.mediaId = options.mediaId;
        makeObservable(this);
    }

    private labelValidator = createValidator(validateLength(1, 50));
    private slugValidator = createValidator(validateLength(1, 50), validateSlug());

    private checkConflictLabel = conflictChecker(
        (b) => (this.isLabelChecking = b),
        (b) => (this.isLabelConflicted = b),
        async (currentValue) => {
            const input = new TagSearchInput({label: currentValue, mediaId: this.mediaId});
            input.label = currentValue;
            const result = await this.adminApi.getTags(input);
            return result.tags.length > 0;
        },
    );

    private checkConflictSlug = conflictChecker(
        (b) => (this.isSlugChecking = b),
        (b) => (this.isSlugConflicted = b),
        async (currentValue) => {
            const input = new TagSearchInput({label: currentValue, mediaId: this.mediaId});
            input.slug = currentValue;
            const result = await this.adminApi.getTags(input);
            return result.tags.length > 0;
        },
    );

    public setOnCreated(cb: () => void) {
        this.onCreatedCallback = cb;
    }

    @computed
    public get canCreate() {
        return (
            this.label.trim().length > 0 &&
            this.slug.trim().length > 0 &&
            this.labelValidationResult.isEmpty &&
            this.slugValidationResult.isEmpty &&
            !this.isLabelConflicted &&
            !this.isSlugConflicted &&
            !this.isLabelChecking &&
            !this.isSlugChecking
        );
    }

    @action.bound
    public onInteraction(nextOpenState: boolean) {
        this.setLabel("");
        this.setSlug("");
        this.isOpen = nextOpenState;
    }

    @action.bound
    public close() {
        this.isOpen = false;
    }

    @action.bound
    public async create(onCreated: () => void) {
        await this.setIsCreating(true);
        try {
            const input = new TagAddInput();
            input.label = this.label;
            input.slug = this.slug;
            await this.adminApi.postTag({mediaId: this.mediaId}, input);
            onCreated();
            if (this.onCreatedCallback) {
                this.onCreatedCallback();
            }
        } catch (e) {
            console.error(e);
        } finally {
            await this.setIsCreating(false);
            this.close();
        }
    }

    @action.bound
    public async setLabel(s: string) {
        this.label = s;
        (await this.labelValidator(s)).run((v) => (this.labelValidationResult = v));
        if (this.labelValidationResult.isEmpty) {
            this.checkConflictLabel("", this.label);
        }
    }

    @action.bound
    public async setSlug(s: string) {
        this.slug = s;
        (await this.slugValidator(s)).run((v) => (this.slugValidationResult = v));
        if (this.slugValidationResult.isEmpty) {
            this.checkConflictSlug("", this.slug);
        }
    }

    @action.bound
    private async setIsCreating(b: boolean) {
        this.isCreating = b;
    }
}
